import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from "react-router-dom";
import { Container, Row, Col } from 'react-bootstrap';
import {
  FaWallet, FaMoneyBillWave, FaClock, FaCheckCircle, FaPercentage,
  FaSearch, FaPlus, FaTimes, FaUniversity, FaSave, FaEye
} from 'react-icons/fa';
import axios from 'axios';
import Button from '../../../components/general/button/Button';
import Pagination from '../../../components/general/pagination/Pagination';
import Swal from 'sweetalert2';
import '../../../assets/css/seller/earnings/SellerEarnings.css';
import SellerLayout from '../../../layouts/SellerLayout';
import MyContext from '../../../context/MyContext';
import { SellerEarningsPageSkeleton } from '../../../components/general/skeleton/SkeletonBlock';

const SellerEarnings = () => {
  const navigate = useNavigate();
  const { baseUrl, baseUrlForFiles, authToken } = useContext(MyContext);

  const [view, setView] = useState('table');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);

  const [totalEarning, setTotalEarning] = useState(0);
  const [commissionRate, setCommissionRate] = useState(0);
  const [payoutRequested, setPayoutRequested] = useState(0);
  const [completedPayout, setCompletedPayout] = useState(0);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);

  const [bankAccounts, setBankAccounts] = useState([]);

  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [withdrawalBankAccount, setWithdrawalBankAccount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const stats = [
    { icon: <FaMoneyBillWave />, color: 'green', label: 'Total Earning', value: `PKR ${totalEarning.toLocaleString()}` },
    { icon: <FaClock />, color: 'orange', label: 'Payout Requested', value: `PKR ${payoutRequested.toLocaleString()}` },
    { icon: <FaCheckCircle />, color: 'purple', label: 'Completed Payout', value: `PKR ${completedPayout.toLocaleString()}` },
    { icon: <FaWallet />, color: 'blue', label: 'Available Balance', value: `PKR ${availableBalance.toLocaleString()}` },
    { icon: <FaPercentage />, color: 'red', label: 'Platform Commission Rate', value: `${commissionRate}%` },
  ];

  const viewEarningsAndTransactions = () => {
    setLoading(true);

    axios
      .get(`${baseUrl}/seller/earning/viewEarningsAndTransactions`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({ icon: "error", title: "Error", text: response.data.error });
        } else {
          setTotalEarning(Number(response.data.total_earning));
          setCommissionRate(Number(response.data.commission_rate));
          setPayoutRequested(Number(response.data.payout_requested));
          setCompletedPayout(Number(response.data.completed_payout));
          setAvailableBalance(Number(response.data.available_balance));
          setTransactions(response.data.transactions);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load earnings." });
      });
  };

  const viewAllBankAccounts = () => {
    axios
      .get(`${baseUrl}/seller/earning/viewAllBankAccounts`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success) {
          setBankAccounts(response.data.accounts);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      navigate("/seller/login");
      return;
    }
    viewEarningsAndTransactions();
    viewAllBankAccounts();
  }, [authToken]);

  const getStatusClass = (status) => {
    const statusMap = {
      'Completed': 'earnings-status-completed',
      'Processing': 'earnings-status-processing',
      'Pending': 'earnings-status-pending',
      'Failed': 'earnings-status-failed'
    };
    return statusMap[status] || 'earnings-status-pending';
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getTransactionType = (txn) => txn.kmorder_id ? 'Order Payment' : 'Withdrawal';
  const getTransactionOrderId = (txn) => txn.kmorder_id ? `#${txn.kmorder_id}` : '-';
  const getTransactionAmountDisplay = (txn) => txn.kmorder_id ? Number(txn.amount) : -Number(txn.amount);
  const getTransactionMethod = (txn) => txn.kmsellerbank ? `${txn.kmsellerbank.bankname} - ${txn.kmsellerbank.accountnumber}` : 'Order Payment';

  const filteredTransactions = transactions.filter(txn => {
    const matchesSearch =
      String(txn.id).includes(searchText.toLowerCase()) ||
      getTransactionType(txn).toLowerCase().includes(searchText.toLowerCase());
    const matchesStatus = statusFilter === 'all' || txn.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentTransactions = filteredTransactions.slice(indexOfFirst, indexOfLast);

  if (loading) {
    return (
      <SellerLayout>
        <SellerEarningsPageSkeleton />
      </SellerLayout>
    );
  }

  return (
    <SellerLayout>
    <div className="earnings-container">
      <Container fluid>

        <div className="earnings-header">
          <div>
            <h1>Earnings & Transactions</h1>
            <p>Manage your earnings and withdrawals</p>
          </div>
          {view === 'table' && (
            <Button
              variant="gradient"
              icon={<FaPlus />}
              onClick={() => {
                setWithdrawalAmount('');
                setWithdrawalBankAccount('');
                setView('withdrawal');
              }}
            >
              Request Withdrawal
            </Button>
          )}
        </div>

        {view === 'table' && (
          <Row className="earnings-stats g-3 g-lg-4">
            {stats.slice(0, 4).map((stat, index) => (
              <Col key={index} xs={12} sm={6} xl={3}>
                <div className="earnings-stat-card">
                  <div className={`earnings-stat-icon ${stat.color}`}>
                    {stat.icon}
                  </div>
                  <div className="earnings-stat-content">
                    <div className="earnings-stat-label">{stat.label}</div>
                    <div className="earnings-stat-value">{stat.value}</div>
                  </div>
                </div>
              </Col>
            ))}

            <Col xs={12} sm={6} xl={3}>
              <div
                className="earnings-stat-card earnings-stat-card--commission"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  borderRadius: '12px',
                  padding: '16px 20px',
                }}
              >
                <div
                  className="earnings-stat-icon red"
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    color:'red',
                    flexShrink: 0,
                  }}
                >
                  {stats[4].icon}
                </div>
                <div className="earnings-stat-content" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <div className="earnings-stat-label" style={{ color: 'black', margin: 0 }}>
                    {stats[4].label}:
                  </div>
                  <div className="earnings-stat-value" style={{ color: 'black', fontWeight: 700, fontSize: '20px' }}>
                    {stats[4].value}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        )}

        {view === 'table' ? (
          <div className="earnings-table-section">
            <div className="earnings-table-header">
              <div className="earnings-search">
                <FaSearch />
                <input
                  type="text"
                  placeholder="Search transactions..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
              <div className="earnings-filters">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="earnings-status-filter"
                >
                  <option value="all">All Status</option>
                  <option value="Completed">Completed</option>
                  <option value="Processing">Processing</option>
                  <option value="Pending">Pending</option>
                  <option value="Failed">Failed</option>
                </select>
              </div>
            </div>

            <div className="earnings-table-wrapper">
              <table className="earnings-table">
                <thead>
                  <tr>
                    <th>TRANSACTION ID</th>
                    <th>TYPE</th>
                    <th>ORDER ID</th>
                    <th>AMOUNT</th>
                    <th>STATUS</th>
                    <th>METHOD</th>
                    <th>DATE</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={8} style={{ textAlign: "center", padding: "40px" }}>
                        <div className="ksa-spinner"></div>
                      </td>
                    </tr>
                  ) : currentTransactions.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ textAlign: 'center', padding: '20px' }}>
                        No transactions found.
                      </td>
                    </tr>
                  ) : (
                    currentTransactions.map((txn) => {
                      const amountDisplay = getTransactionAmountDisplay(txn);
                      return (
                        <tr key={txn.id}>
                          <td className="earnings-txn-id">TXN-{txn.id}</td>
                          <td>{getTransactionType(txn)}</td>
                          <td>{getTransactionOrderId(txn)}</td>
                          <td className={`earnings-amount ${amountDisplay < 0 ? 'negative' : 'positive'}`}>
                            {amountDisplay < 0 ? '-' : '+'} PKR {Math.abs(amountDisplay).toLocaleString()}
                          </td>
                          <td>
                            <span className={`earnings-status ${getStatusClass(txn.status)}`}>
                              {txn.status}
                            </span>
                          </td>
                          <td>{getTransactionMethod(txn)}</td>
                          <td>{formatDate(txn.created_at)}</td>
                          <td>
                            <button
                              className="ksa-icon-action-btn"
                              title="View Details"
                              onClick={() => setSelectedTransaction(txn)}
                            >
                              <FaEye />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalItems={filteredTransactions.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(items) => {
                setItemsPerPage(items);
                setCurrentPage(1);
              }}
            />
          </div>
        ) : (
          <div className="earnings-withdrawal-section">
            <div className="earnings-withdrawal-header">
              <h3>Request Withdrawal</h3>
              <button className="earnings-withdrawal-close" onClick={() => setView('table')}>
                <FaTimes />
              </button>
            </div>

            <div className="earnings-withdrawal-body">
              <Row className="g-4">
                <Col xs={12}>
                  <div className="earnings-balance-info">
                    <FaWallet />
                    <div>
                      <p>Available Balance</p>
                      <h3>PKR {availableBalance.toLocaleString()}</h3>
                    </div>
                  </div>
                </Col>

                <Col xs={12} md={6}>
                  <div className="earnings-form-group">
                    <label>Withdrawal Amount *</label>
                    <input
                      type="number"
                      value={withdrawalAmount}
                      onChange={(e) => setWithdrawalAmount(e.target.value)}
                      placeholder="Enter amount"
                    />
                  </div>
                </Col>

                <Col xs={12} md={6}>
                  <div className="earnings-form-group">
                    <label>Select Bank Account *</label>
                    <select
                      value={withdrawalBankAccount}
                      onChange={(e) => setWithdrawalBankAccount(e.target.value)}
                    >
                      <option value="">Select Account</option>
                      {bankAccounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.bankname} - {account.accountnumber}
                        </option>
                      ))}
                    </select>
                  </div>
                </Col>

                <Col xs={12}>
                  <div className="earnings-info-box">
                    <FaUniversity />
                    <div>
                      <h6>Withdrawal Information</h6>
                      <ul>
                        <li>Processing time: 2-3 business days</li>
                        <li>Platform service charge already deducted from your balance</li>
                      </ul>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>

            <div className="earnings-withdrawal-footer">
              <Button variant="outline" onClick={() => setView('table')}>
                Cancel
              </Button>
              <Button
                variant="gradient"
                icon={<FaSave />}
                onClick={() => {
                  if (!withdrawalAmount || !withdrawalBankAccount) {
                    Swal.fire({ icon: "warning", title: "Missing Info", text: "Please fill all required fields" });
                    return;
                  }

                  const amount = parseFloat(withdrawalAmount);

                  if (amount <= 0) {
                    Swal.fire({ icon: "warning", title: "Invalid Amount", text: "Enter a valid amount" });
                    return;
                  }

                  if (amount > availableBalance) {
                    Swal.fire({ icon: "error", title: "Error", text: "Insufficient balance" });
                    return;
                  }

                  setSubmitting(true);
                  const formData = new FormData();
                  formData.append("kmsellerbank_id", withdrawalBankAccount);
                  formData.append("amount", amount);

                  axios
                    .post(`${baseUrl}/seller/earning/requestWithdrawal`, formData, {
                      headers: { Authorization: `Bearer ${authToken}` },
                    })
                    .then((response) => {
                      setSubmitting(false);
                      if (response.data.hasOwnProperty("error")) {
                        Swal.fire({ icon: "error", title: "Error", text: response.data.error });
                      } else if (response.data.success === false) {
                        Swal.fire({ icon: "warning", title: "Withdrawal Issue", text: response.data.message });
                      } else {
                        Swal.fire({ icon: "success", title: "Success", text: "Withdrawal request submitted successfully!" });
                        setView('table');
                        viewEarningsAndTransactions();
                      }
                    })
                    .catch((error) => {
                      setSubmitting(false);
                      console.log(error);
                      Swal.fire({ icon: "error", title: "Error", text: "Failed to submit withdrawal request." });
                    });
                }}
                disabled={submitting}
              >
                Submit Request
              </Button>
            </div>
          </div>
        )}

        {selectedTransaction && (
          <div
            className="ksa-modal-overlay"
            onClick={() => setSelectedTransaction(null)}
          >
            <div
              className="ksa-modal ksa-modal-lg ksa-category-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="ksa-category-modal-header">
                <h3 className="ksa-category-modal-title">Transaction Details</h3>
                <button
                  className="ksa-modal-close"
                  onClick={() => setSelectedTransaction(null)}
                >
                  <FaTimes size={18} />
                </button>
              </div>

              <div className="ksa-category-modal-body">
                <div className="earnings-summary-row">
                  <span>Transaction ID</span>
                  <span>TXN-{selectedTransaction.id}</span>
                </div>
                <div className="earnings-summary-row">
                  <span>Type</span>
                  <span>{getTransactionType(selectedTransaction)}</span>
                </div>
                <div className="earnings-summary-row">
                  <span>Status</span>
                  <span className={`earnings-status ${getStatusClass(selectedTransaction.status)}`}>
                    {selectedTransaction.status}
                  </span>
                </div>
                <div className="earnings-summary-row">
                  <span>Date</span>
                  <span>{formatDate(selectedTransaction.created_at)}</span>
                </div>

                {selectedTransaction.kmorder ? (
                  <>
                    <div className="earnings-order-items-wrapper">
                      <table className="earnings-order-items-table">
                        <thead>
                          <tr>
                            <th>PRODUCT</th>
                            <th>QTY</th>
                            <th>PRICE</th>
                            <th>AMOUNT</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedTransaction.kmorder.kmcarts?.map((item) => (
                            <tr key={item.id}>
                              <td>
                                <div className="earnings-item-product">
                                  <img
                                    src={`${baseUrlForFiles}/${item.kmproduct?.image}`}
                                    alt={item.kmproduct?.name}
                                    className="earnings-item-img"
                                  />
                                  <span>{item.kmproduct?.name}</span>
                                </div>
                              </td>
                              <td>{item.quantity}</td>
                              <td>PKR {Number(item.amountPerPiece).toLocaleString()}</td>
                              <td>PKR {Number(item.total_price).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="earnings-order-summary">
                      <div className="earnings-summary-row total">
                        <span>Order Total</span>
                        <span>
                          PKR {Number(selectedTransaction.kmorder.totalPrice).toLocaleString()}
                        </span>
                      </div>
                      <div className="earnings-summary-row commission">
                        <span>Platform Commission ({commissionRate}%)</span>
                        <span>
                          - PKR {(Number(selectedTransaction.kmorder.totalPrice) * (commissionRate / 100)).toLocaleString()}
                        </span>
                      </div>
                      <div className="earnings-summary-row net">
                        <span>Net Earning</span>
                        <span>
                          PKR {(Number(selectedTransaction.kmorder.totalPrice) * (1 - commissionRate / 100)).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="earnings-summary-row total">
                      <span>Amount</span>
                      <span>PKR {Number(selectedTransaction.amount).toLocaleString()}</span>
                    </div>

                    {selectedTransaction.kmsellerbank && (
                      <div className="earnings-summary-row">
                        <span>Bank Account</span>
                        <span>
                          {selectedTransaction.kmsellerbank.bankname} - {selectedTransaction.kmsellerbank.accountnumber}
                        </span>
                      </div>
                    )}

                    {selectedTransaction.status === 'Completed' && selectedTransaction.recieptImage ? (
                      <div className="earnings-summary-row">
                        <span>Transfer Receipt</span>
                        <a
                          href={`${baseUrlForFiles}/${selectedTransaction.recieptImage}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 14px',
                            background: 'var(--gradient-primary, linear-gradient(135deg, #097d43 0%, #0ac46c 100%))',
                            color: '#ffffff',
                            borderRadius: '50px',
                            fontWeight: 600,
                            fontSize: '13px',
                            textDecoration: 'none',
                            boxShadow: '0 4px 8px rgba(9,125,67,0.3)',
                          }}
                        >
                          View Receipt
                        </a>
                      </div>
                    ) : selectedTransaction.status === 'Pending' ? (
                      <div className="earnings-summary-row">
                        <span>Transfer Receipt</span>
                        <span
                          style={{
                            padding: '4px 12px',
                            background: '#fff8e1',
                            color: '#ff9800',
                            borderRadius: '50px',
                            fontSize: '13px',
                            fontWeight: 600,
                          }}
                        >
                          Awaiting admin approval
                        </span>
                      </div>
                    ) : null}
                  </>
                )}
              </div>

              <div className="ksa-category-modal-footer">
                <button
                  className="outline-gradient"
                  onClick={() => setSelectedTransaction(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

      </Container>
    </div>
    </SellerLayout>
  );
};

export default SellerEarnings;
